import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import { ActionContext } from 'hooks/useAdminListPage';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { NetworkStatusClientSummary } from 'models/GroupPayerNetworkStatus';
import {
  adminIndexNetworkStatusClients,
  groupPayerNetworkStatusQueryKeys,
} from 'services/api/groupPayerNetworkStatuses';
import { BodySmall } from 'styles/typography';

type Props = {
  onClose: () => void;
  ctx: ActionContext;
  clientPath: (clientId: string) => string;
};

// Nothing is created here: a client joins the tab once its first payer is added, from its own page.
// The picker offers only clients with a facility and nothing configured yet.
export default function AddPayerNetworkStatusModal({ onClose, ctx, clientPath }: Props) {
  const [client, setClient] = useState<NetworkStatusClientSummary | null>(null);

  const params = { sortBy: 'name asc', eligible: true };
  const clientOptions = useInfiniteOptions<NetworkStatusClientSummary>({
    queryKey: groupPayerNetworkStatusQueryKeys.clients(params),
    fetchFn: adminIndexNetworkStatusClients,
    params,
  });

  const handleContinue = () => {
    if (!client) return;
    onClose();
    ctx.navigate(clientPath(client.id));
  };

  return (
    <StandardModal title='Add Payer Network Status' onCancel={onClose}>
      <Description>
        Select a client to set up its payer network status. You&rsquo;ll add payers to its facilities next.
      </Description>

      <FieldContainer>
        <FieldLabel>Client</FieldLabel>
        <Select<NetworkStatusClientSummary>
          {...clientOptions}
          value={client}
          onChange={setClient}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          placeholder='Search clients'
          dataTestId='client-select'
        />
      </FieldContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleContinue} disabled={!client}>
          Continue
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;
